import { useState } from 'react';
import { api, clearSession, type Session } from './api.js';
import { wipeEverything } from './crypto/store.js';
import { Avatar, BackIcon } from './ui.js';
import { getLanguage, languageNames, setLanguage, useT, type Lang } from './i18n.js';

/**
 * Einstellungen des angemeldeten Nutzers.
 *
 * Bewusst klein gehalten: Passwort ändern und Gerät zurücksetzen. Alles, was
 * darüber hinausginge — Profilbild, Statustext, Telefonnummer —, wären
 * zusätzliche Daten beim Server, und der soll so wenig wie möglich wissen.
 */
export function Settings({
  session,
  onClose,
  onLogout,
}: {
  session: Session;
  onClose: () => void;
  onLogout: () => void;
}) {
  const t = useT();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [confirmWipe, setConfirmWipe] = useState(false);

  async function changePassword(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);

    if (next.length < 8) {
      setError(t('errPasswordTooShort'));
      return;
    }
    if (next !== repeat) {
      setError('Die beiden Passwörter stimmen nicht überein.');
      return;
    }

    setBusy(true);
    try {
      await api.changePassword(session.handle, current, next);
      setCurrent('');
      setNext('');
      setRepeat('');
      setDone(true);
    } catch (err) {
      const message = (err as Error).message;
      setError(
        message.includes('invalid_credentials')
          ? t('errCurrentPassword')
          : message.includes('password_unchanged')
            ? t('errPasswordUnchanged')
            : `Änderung fehlgeschlagen: ${message}`,
      );
    } finally {
      setBusy(false);
    }
  }

  /** Schlüssel und Verlauf löschen. Nicht umkehrbar, daher zweistufig. */
  async function wipe() {
    await wipeEverything();
    clearSession();
    onLogout();
  }

  return (
    <div className="settings">
      <header className="thread-head">
        <button className="icon-btn" onClick={onClose} aria-label={t('close')}>
          <BackIcon />
        </button>
        <h2>{t('settings')}</h2>
      </header>

      <div className="settings-body">
        <div className="settings-me">
          <Avatar name={session.handle} />
          <div>
            <strong>{session.handle}</strong>
            <span className="hint" style={{ display: 'block', margin: 0 }}>
              {t('deviceNote')}
            </span>
          </div>
        </div>

        {/* Sprache zuerst: Wer die Oberfläche nicht lesen kann, findet alles
            andere nicht. */}
        <section className="card">
          <h3>{t('language')}</h3>
          <div className="lang-row">
            {(Object.keys(languageNames) as Lang[]).map((code) => (
              <button
                key={code}
                type="button"
                className={code === getLanguage() ? 'btn btn-sm' : 'btn btn-ghost btn-sm'}
                onClick={() => setLanguage(code)}
                lang={code}
              >
                {languageNames[code]}
              </button>
            ))}
          </div>
          <p className="hint" style={{ marginBottom: 0 }}>
            {t('languageHint')}
          </p>
        </section>

        <section className="card">
          <h3>{t('changePassword')}</h3>

          <form onSubmit={changePassword}>
            <label className="label" htmlFor="cur-pw">
              {t('currentPassword')}
            </label>
            <input
              id="cur-pw"
              className="input"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              disabled={busy}
            />

            <label className="label" htmlFor="next-pw" style={{ marginTop: '0.75rem' }}>
              {t('newPassword')}
            </label>
            <input
              id="next-pw"
              className="input"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              disabled={busy}
            />

            <label className="label" htmlFor="rep-pw" style={{ marginTop: '0.75rem' }}>
              {t('repeat')}
            </label>
            <input
              id="rep-pw"
              className="input"
              type="password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              autoComplete="new-password"
              disabled={busy}
            />

            <button
              className="btn"
              style={{ width: '100%', marginTop: '1rem' }}
              type="submit"
              disabled={busy || !current || !next}
            >
              {busy ? t('changing') : t('changePassword')}
            </button>
          </form>

          {done && (
            <div className="alert" style={{ marginTop: '0.9rem', background: 'var(--bg-input)' }}>
              {t('passwordChanged')}
            </div>
          )}
          {error && (
            <div className="alert alert-error" style={{ marginTop: '0.9rem' }}>
              {error}
            </div>
          )}

          <p className="hint">
            {t('passwordNote')}
          </p>
        </section>

        <section className="card">
          <h3>{t('signOut')}</h3>
          <p className="hint" style={{ marginTop: 0 }}>
            {t('signOutNote')}
          </p>
          <button
            className="btn btn-ghost"
            style={{ marginTop: '0.75rem' }}
            onClick={() => {
              clearSession();
              onLogout();
            }}
          >
            {t('signOut')}
          </button>
        </section>

        <section className="card">
          <h3>{t('resetDevice')}</h3>
          <p className="hint hint-warn" style={{ marginTop: 0 }}>
            {t('resetDeviceNote')}
          </p>

          {confirmWipe ? (
            <div className="row" style={{ marginTop: '0.75rem' }}>
              <button className="btn" style={{ background: 'var(--danger)' }} onClick={wipe}>
                {t('confirmWipe')}
              </button>
              <button className="btn btn-ghost" onClick={() => setConfirmWipe(false)}>
                {t('cancel')}
              </button>
            </div>
          ) : (
            <button
              className="btn btn-ghost"
              style={{ marginTop: '0.75rem', color: 'var(--danger)' }}
              onClick={() => setConfirmWipe(true)}
            >
              {t('reset')}
            </button>
          )}
        </section>
      </div>
    </div>
  );
}
